import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabase'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', color: 'var(--text)',
  width: '100%', boxSizing: 'border-box', outline: 'none'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

const loanTypes = ['mortgage', 'auto', 'student', 'personal', 'credit']
const loanTypeLabels = { mortgage: 'Home Loan', auto: 'Auto Loan', student: 'Education Loan', personal: 'Personal Loan', credit: 'Credit Card' }

export default function AdminSponsors({ session }) {
  const [sponsors, setSponsors] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingSponsor, setEditingSponsor] = useState(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const [form, setForm] = useState({
    name: '', logo_url: '', description: '',
    interest_rate_from: '', interest_rate_to: '',
    website_url: '', loan_types: [], display_order: 0, is_active: true
  })

  useEffect(() => { fetchSponsors() }, [])

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  async function fetchSponsors() {
    const { data } = await supabase
      .from('sponsor_partners')
      .select('*')
      .order('display_order', { ascending: true })
    if (data) setSponsors(data)
    setLoading(false)
  }

  function resetForm() {
    setForm({ name: '', logo_url: '', description: '', interest_rate_from: '', interest_rate_to: '', website_url: '', loan_types: [], display_order: 0, is_active: true })
    setEditingSponsor(null)
  }

  function openEdit(sponsor) {
    setEditingSponsor(sponsor)
    setForm({
      name: sponsor.name || '',
      logo_url: sponsor.logo_url || '',
      description: sponsor.description || '',
      interest_rate_from: sponsor.interest_rate_from || '',
      interest_rate_to: sponsor.interest_rate_to || '',
      website_url: sponsor.website_url || '',
      loan_types: sponsor.loan_types || [],
      display_order: sponsor.display_order || 0,
      is_active: sponsor.is_active !== false
    })
    setShowForm(true)
  }

  function toggleLoanType(type) {
    setForm(prev => ({
      ...prev,
      loan_types: prev.loan_types.includes(type)
        ? prev.loan_types.filter(t => t !== type)
        : [...prev.loan_types, type]
    }))
  }

  async function saveSponsor() {
    if (!form.name.trim()) { showToast('Please enter sponsor name'); return }
    setSaving(true)
    const data = {
      name: form.name.trim(),
      logo_url: form.logo_url || null,
      description: form.description || null,
      interest_rate_from: form.interest_rate_from ? parseFloat(form.interest_rate_from) : null,
      interest_rate_to: form.interest_rate_to ? parseFloat(form.interest_rate_to) : null,
      website_url: form.website_url || null,
      loan_types: form.loan_types,
      display_order: parseInt(form.display_order) || 0,
      is_active: form.is_active
    }
    let error
    if (editingSponsor) {
      ({ error } = await supabase.from('sponsor_partners').update(data).eq('id', editingSponsor.id))
    } else {
      ({ error } = await supabase.from('sponsor_partners').insert(data))
    }
    if (error) { showToast('❌ Error: ' + error.message) }
    else { showToast('✅ Sponsor saved!'); fetchSponsors(); setShowForm(false); resetForm() }
    setSaving(false)
  }

  async function toggleSponsorActive(sponsor) {
    const { error } = await supabase
      .from('sponsor_partners')
      .update({ is_active: !sponsor.is_active })
      .eq('id', sponsor.id)
    if (error) { showToast('❌ Error: ' + error.message) }
    else { showToast(sponsor.is_active ? '⛔ Sponsor hidden' : '✅ Sponsor activated'); fetchSponsors() }
  }

  async function deleteSponsor(id) {
    if (!window.confirm('Delete this sponsor?')) return
    const { error } = await supabase.from('sponsor_partners').delete().eq('id', id)
    if (error) { showToast('❌ Error: ' + error.message) }
    else { showToast('🗑️ Sponsor deleted'); fetchSponsors() }
  }

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#0d0d0d', color: '#f5f0e8', padding: '0.7rem 1.1rem', borderRadius: '6px', fontSize: '0.8rem', zIndex: 9999, borderLeft: '3px solid #c9a84c' }}>{toast}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.8rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Sponsor Partners</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Manage lending partners shown to users as recommendations.</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true) }}
          style={{ background: '#0d0d0d', color: '#f5f0e8', border: 'none', padding: '0.55rem 1.1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          + Add Sponsor
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showForm ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>

        {/* Sponsors List */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>All Sponsors ({sponsors.length})</span>
          </div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : sponsors.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🤝</div>
              <div style={{ fontWeight: '500', marginBottom: '0.3rem' }}>No sponsors yet</div>
              <div style={{ fontSize: '0.8rem' }}>Add lending partners to recommend to users.</div>
            </div>
          ) : (
            sponsors.map((sp, i) => (
              <div key={sp.id} style={{ padding: '1rem 1.4rem', borderBottom: i < sponsors.length - 1 ? '1px solid #d4cfc5' : 'none', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: '600', fontSize: '0.88rem', color: 'var(--text)' }}>{sp.name}</span>
                      {!sp.is_active && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(184,68,26,0.1)', color: '#b8441a', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>HIDDEN</span>}
                    </div>
                    {sp.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>{sp.description}</div>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
                      {(sp.loan_types || []).map(t => (
                        <span key={t} style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'var(--surface-hdr)', color: 'var(--text)', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>{loanTypeLabels[t] || t}</span>
                      ))}
                    </div>
                    {(sp.interest_rate_from || sp.interest_rate_to) && (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#4a6741' }}>
                        Rate: {sp.interest_rate_from}% – {sp.interest_rate_to}% p.a.
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button onClick={() => openEdit(sp)} style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)' }}>Edit</button>
                    <button onClick={() => toggleSponsorActive(sp)} style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: sp.is_active ? '#b8441a' : '#4a6741' }}>{sp.is_active ? 'Hide' : 'Show'}</button>
                    <button onClick={() => deleteSponsor(sp.id)} style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: '#c0392b' }}>Del</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>{editingSponsor ? 'Edit Sponsor' : 'Add Sponsor'}</span>
              <button onClick={() => { setShowForm(false); resetForm() }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.2rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div><label style={labelStyle}>Partner Name *</label><input style={inputStyle} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. SBI Bank" /></div>
              <div><label style={labelStyle}>Logo URL</label><input style={inputStyle} value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." /></div>
              <div><label style={labelStyle}>Description</label><textarea style={{ ...inputStyle, height: '70px', resize: 'vertical', fontFamily: 'sans-serif' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of this partner..." /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <div><label style={labelStyle}>Rate From (%)</label><input style={inputStyle} type="text" inputMode="decimal" value={form.interest_rate_from} onChange={e => setForm(p => ({ ...p, interest_rate_from: e.target.value }))} placeholder="7.5" /></div>
                <div><label style={labelStyle}>Rate To (%)</label><input style={inputStyle} type="text" inputMode="decimal" value={form.interest_rate_to} onChange={e => setForm(p => ({ ...p, interest_rate_to: e.target.value }))} placeholder="12.5" /></div>
              </div>
              <div><label style={labelStyle}>Website URL</label><input style={inputStyle} value={form.website_url} onChange={e => setForm(p => ({ ...p, website_url: e.target.value }))} placeholder="https://sbi.co.in" /></div>
              <div>
                <label style={labelStyle}>Loan Types</label>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {loanTypes.map(t => (
                    <button key={t} onClick={() => toggleLoanType(t)} style={{ padding: '0.25rem 0.6rem', borderRadius: '3px', border: '1px solid', fontFamily: 'monospace', fontSize: '0.62rem', cursor: 'pointer', borderColor: form.loan_types.includes(t) ? '#0d0d0d' : '#d4cfc5', background: form.loan_types.includes(t) ? '#0d0d0d' : 'transparent', color: form.loan_types.includes(t) ? '#f5f0e8' : '#8a8278' }}>
                      {loanTypeLabels[t]}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <div><label style={labelStyle}>Display Order</label><input style={inputStyle} type="text" inputMode="numeric" value={form.display_order} onChange={e => setForm(p => ({ ...p, display_order: e.target.value }))} placeholder="0" /></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <label style={labelStyle}>Status</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                    Active (visible to users)
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                <button onClick={() => { setShowForm(false); resetForm() }} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}>Cancel</button>
                <button onClick={saveSponsor} disabled={saving} style={{ background: '#0d0d0d', color: '#f5f0e8', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', textTransform: 'uppercase' }}>
                  {saving ? 'Saving...' : 'Save Sponsor'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}